<template>
    <Teleport to="body">
        <div v-if="open" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div class="absolute inset-0 bg-black/40" @click="close"></div>

            <div class="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
                <!-- ===== Tela 1: lista de tags ===== -->
                <template v-if="view === 'list'">
                    <div class="flex items-center justify-between px-5 py-4 border-b">
                        <h3 class="text-base font-semibold text-gray-900">Tags</h3>
                        <button type="button" @click="close" class="p-1 text-gray-400 hover:text-gray-600">
                            <XMarkIcon class="w-5 h-5" />
                        </button>
                    </div>

                    <div class="flex-1 overflow-y-auto">
                        <div v-if="loading" class="px-5 py-8 text-center text-sm text-gray-500">Carregando...</div>
                        <div v-else-if="tags.length === 0" class="px-5 py-8 text-center text-sm text-gray-500">
                            Nenhuma tag cadastrada
                        </div>
                        <ul v-else class="divide-y divide-gray-100">
                            <li v-for="tag in tags" :key="tag.id" class="flex items-center gap-3 px-5 py-3">
                                <span class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: tag.color }"></span>
                                <span class="flex-1 min-w-0 text-sm text-gray-900 truncate">{{ tag.name }}</span>
                                <span class="text-xs text-gray-500 whitespace-nowrap">
                                    {{ countOf(tag) }} {{ countOf(tag) === 1 ? 'contato' : 'contatos' }}
                                </span>
                                <button type="button" @click="openForm(tag)" title="Editar"
                                    class="p-1.5 text-gray-400 hover:text-gray-700">
                                    <PencilSquareIcon class="w-4 h-4" />
                                </button>
                                <button type="button" @click="askDelete(tag)"
                                    :disabled="countOf(tag) > 0"
                                    :title="countOf(tag) > 0 ? 'Não é possível excluir: há contatos vinculados' : 'Excluir'"
                                    class="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-400">
                                    <TrashIcon class="w-4 h-4" />
                                </button>
                            </li>
                        </ul>
                    </div>

                    <!-- Confirmação de exclusão -->
                    <div v-if="tagToDelete" class="px-5 py-3 bg-red-50 border-t border-red-100 flex items-center gap-3">
                        <p class="flex-1 text-sm text-red-700">
                            Excluir a tag <strong>{{ tagToDelete.name }}</strong>?
                        </p>
                        <button type="button" @click="tagToDelete = null" class="text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
                        <button type="button" @click="confirmDelete" :disabled="deleting"
                            class="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md disabled:opacity-50">
                            {{ deleting ? 'Excluindo...' : 'Excluir' }}
                        </button>
                    </div>
                    <p v-if="error" class="px-5 py-2 text-sm text-red-600 border-t">{{ error }}</p>

                    <div class="px-5 py-4 border-t flex justify-between items-center">
                        <button type="button" @click="close" class="secondaryButton">Fechar</button>
                        <button type="button" @click="openForm(null)" class="primaryButton flex items-center gap-1">
                            <PlusIcon class="w-4 h-4" /> Nova tag
                        </button>
                    </div>
                </template>

                <!-- ===== Tela 2: criar / editar ===== -->
                <template v-else>
                    <div class="flex items-center gap-2 px-5 py-4 border-b">
                        <button type="button" @click="backToList" class="p-1 text-gray-400 hover:text-gray-600">
                            <ArrowLeftIcon class="w-5 h-5" />
                        </button>
                        <h3 class="text-base font-semibold text-gray-900">{{ form.id ? 'Editar tag' : 'Nova tag' }}</h3>
                    </div>

                    <div class="flex-1 overflow-y-auto p-5 space-y-5">
                        <div>
                            <label class="block text-sm text-gray-700 mb-1">Nome *</label>
                            <input type="text" v-model="form.name" maxlength="100" placeholder="Ex: Cliente"
                                @keyup.enter="save"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400" />
                        </div>

                        <div>
                            <label class="block text-sm text-gray-700 mb-2">Cor</label>
                            <div class="grid grid-cols-8 gap-2">
                                <button v-for="color in colors" :key="color" type="button" @click="form.color = color"
                                    class="w-8 h-8 rounded-md transition-all"
                                    :class="form.color === color ? 'ring-2 ring-offset-2 ring-gray-800' : ''"
                                    :style="{ backgroundColor: color }"></button>
                            </div>
                            <div class="flex items-center gap-3 mt-3">
                                <div class="w-8 h-8 rounded-md border border-gray-200" :style="{ backgroundColor: form.color }"></div>
                                <input type="text" v-model="form.color" placeholder="#6366F1"
                                    class="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-1 focus:ring-gray-400" />
                            </div>
                        </div>

                        <div class="bg-gray-50 rounded-lg p-4 flex justify-center">
                            <span class="px-4 py-1.5 rounded-md text-white text-sm font-medium uppercase"
                                :style="{ backgroundColor: form.color }">
                                {{ form.name || 'TAG' }}
                            </span>
                        </div>
                        <p v-if="error" class="text-sm text-red-600">{{ error }}</p>
                    </div>

                    <div class="px-5 py-4 border-t flex justify-between items-center">
                        <button type="button" @click="backToList" class="secondaryButton">Voltar</button>
                        <button type="button" @click="save" :disabled="saving" class="primaryButton">
                            {{ saving ? 'Salvando...' : (form.id ? 'Salvar alterações' : 'Criar tag') }}
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { XMarkIcon, PlusIcon, PencilSquareIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline'

const DEFAULT_COLOR = '#6366F1'

export default {
    name: 'TagsManagerModal',
    components: { XMarkIcon, PlusIcon, PencilSquareIcon, TrashIcon, ArrowLeftIcon },
    props: {
        open: { type: Boolean, default: false },
    },
    // changed: houve criação/edição/exclusão (quem usa recarrega a lista)
    emits: ['close', 'changed'],
    data() {
        return {
            view: 'list',
            tags: [],
            loading: false,
            saving: false,
            deleting: false,
            error: '',
            tagToDelete: null,
            form: { id: null, name: '', color: DEFAULT_COLOR },
            colors: [
                '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899', '#EF4444', '#F97316', '#F59E0B',
                '#EAB308', '#84CC16', '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#3B82F6', '#64748B',
            ],
        }
    },
    watch: {
        open(value) {
            if (value) {
                this.view = 'list'
                this.error = ''
                this.tagToDelete = null
                this.load()
            }
        },
    },
    methods: {
        countOf(tag) {
            return Number(tag.contacts_count) || 0
        },
        async load() {
            this.loading = true
            try {
                const response = await this.$ApiService.get('crm/tags')
                this.tags = response?.rows || response?.data?.rows || response?.data || []
            } catch (e) {
                console.error('Erro ao carregar tags:', e)
                this.tags = []
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        },
        openForm(tag) {
            this.error = ''
            this.tagToDelete = null
            this.form = tag
                ? { id: tag.id, name: tag.name, color: tag.color || DEFAULT_COLOR }
                : { id: null, name: '', color: DEFAULT_COLOR }
            this.view = 'form'
        },
        backToList() {
            this.error = ''
            this.view = 'list'
        },
        async save() {
            const name = (this.form.name || '').trim()
            if (!name) {
                this.error = 'O nome da tag é obrigatório.'
                return
            }
            this.saving = true
            this.error = ''
            try {
                const body = { name, color: this.form.color || DEFAULT_COLOR }
                if (this.form.id) {
                    await this.$ApiService.put(`crm/tags/${this.form.id}`, body)
                } else {
                    await this.$ApiService.post('crm/tags', body)
                }
                this.$emit('changed')
                this.view = 'list'
                await this.load()
            } catch (e) {
                console.error('Erro ao salvar tag:', e)
                this.error = 'Não foi possível salvar a tag.'
            } finally {
                this.saving = false
            }
        },
        askDelete(tag) {
            if (this.countOf(tag) > 0) return
            this.error = ''
            this.tagToDelete = tag
        },
        async confirmDelete() {
            if (!this.tagToDelete || this.countOf(this.tagToDelete) > 0) return
            this.deleting = true
            this.error = ''
            try {
                await this.$ApiService.delete(`crm/tags/${this.tagToDelete.id}`)
                this.tagToDelete = null
                this.$emit('changed')
                await this.load()
            } catch (e) {
                console.error('Erro ao excluir tag:', e)
                this.error = 'Não foi possível excluir a tag.'
            } finally {
                this.deleting = false
            }
        },
    },
}
</script>
